<template>
  <div class="min-h-screen max-w-3xl mx-auto p-6">
    <!-- Заголовок и форма создания/редактирования -->
    <h2 class="text-2xl font-semibold mb-4">
      {{ isEditMode ? "Редактирование клиники" : "Создание клиники" }}
    </h2>
    <form
      @submit.prevent="submitClinic"
      class="bg-white rounded-lg shadow p-4 space-y-4 mb-6"
    >
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input
          v-model="form.name"
          placeholder="Название клиники"
          required
          class="input"
        />
        <input v-model="form.logo" placeholder="URL логотипа" class="input" />
        <input
          v-model="form.address"
          placeholder="Адрес"
          required
          class="input"
        />
        <input v-model="form.city" placeholder="Город" required class="input" />
        <input
          v-model="form.phone_number"
          placeholder="Телефон"
          required
          class="input"
        />
        <input
          v-model="form.working_hours"
          placeholder="Рабочее время"
          class="input"
        />
      </div>
      <div class="flex items-center gap-4">
        <button type="submit" class="btn">
          {{ isEditMode ? "Сохранить" : "Создать клинику" }}
        </button>
        <button
          v-if="isEditMode"
          @click.prevent="cancelEdit"
          class="btn-secondary"
        >
          Отмена
        </button>
      </div>
    </form>

    <!-- Существующие клиники -->
    <!-- Существующие клиники -->
    <h2 class="text-xl font-semibold mt-8">Существующие клиники</h2>
    <div v-if="clinics.length === 0" class="text-gray-500 mt-2">
      Нет добавленных клиник
    </div>
    <div v-else class="space-y-4 mt-4">
      <div
        v-for="clinic in clinics"
        :key="clinic.ID"
        class="flex justify-between items-center border rounded-lg p-4 bg-white hover:shadow-lg transition"
      >
        <!-- Текстовая информация -->
        <div class="flex-1 space-y-1">
          <h3 class="text-lg font-bold text-gray-800">{{ clinic.name }}</h3>
          <p class="text-sm text-gray-600">
            {{ clinic.address }}, {{ clinic.city }}
          </p>
          <p class="text-sm text-gray-500">Тел: {{ clinic.phone_number }}</p>
          <p class="text-sm text-gray-500">Режим: {{ clinic.working_hours }}</p>
        </div>

        <!-- Логотип -->
        <div v-if="clinic.logo" class="ml-4 flex-shrink-0">
          <img
            :src="clinic.logo"
            alt="Logo"
            class="h-12 w-auto rounded object-contain"
          />
        </div>

        <!-- Кнопки -->
        <div class="ml-4 flex flex-nowrap gap-2 overflow-x-auto">
          <button
            @click="editClinic(clinic)"
            class="btn-sm bg-blue-500 hover:bg-blue-600"
          >
            Изменить
          </button>
          <button
            @click="removeClinic(clinic.ID)"
            class="btn-sm bg-red-500 hover:bg-red-600"
          >
            Удалить
          </button>
          <button
            @click="openDoctorForm(clinic.ID)"
            class="btn-sm bg-green-500 hover:bg-green-600"
          >
            Добавить врача
          </button>
          <button
            @click="openClinicDetails(clinic)"
            class="btn-sm bg-purple-500 hover:bg-purple-600"
          >
            Просмотр врачей
          </button>
        </div>
      </div>
    </div>

    <!-- Модал создания доктора -->
    <div
      v-if="showDoctorForm"
      class="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50"
    >
      <div
        class="bg-white rounded p-6 w-full max-w-2xl space-y-4 max-h-[80vh] overflow-y-auto"
      >
        <DoctorCreateForm
          :clinicId="selectedClinicId"
          @success="onDoctorCreated"
          @cancel="showDoctorForm = false"
        />
      </div>
    </div>

    <!-- Модал просмотра докторов -->
    <div
      v-if="selectedClinic"
      class="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50"
    >
      <div
        class="bg-white rounded p-6 w-full max-w-3xl space-y-4 max-h-[80vh] overflow-y-auto"
      >
        <h3 class="text-lg font-bold">
          Доктора в клинике: {{ selectedClinic.name }}
        </h3>
        <DoctorList :doctors="selectedClinic.Doctors || []" />
        <div class="flex justify-end mt-4">
          <button class="btn-secondary" @click="selectedClinic = null">
            Закрыть
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


      <script>
import {
  createClinic,
  deleteClinic,
  getAllClinics,
  updateClinic,
} from "@/services/clinicMicroService";
import { onMounted, ref } from "vue";
import { toast } from "vue3-toastify";
import DoctorCreateForm from "../components/DoctorCreateForm.vue";
import DoctorList from "../components/DoctorList.vue";

export default {
  name: "CreateClinicPage",
  components: { DoctorCreateForm, DoctorList },
  setup() {
    const clinics = ref([]);
    const form = ref({
      name: "",
      logo: "",
      address: "",
      city: "",
      phone_number: "",
      working_hours: "",
    });
    const isEditMode = ref(false);
    const editingClinicId = ref(null);
    const showDoctorForm = ref(false);
    const selectedClinicId = ref(null);
    const selectedClinic = ref(null);

    const fetchClinics = async () => {
      try {
        const res = await getAllClinics();
        clinics.value = res.data;
      } catch (err) {
        toast.error("Ошибка загрузки клиник");
        console.error(err);
      }
    };
    const resetForm = () => {
      form.value = {
        name: "",
        logo: "",
        address: "",
        city: "",
        phone_number: "",
        working_hours: "",
      };
      isEditMode.value = false;
      editingClinicId.value = null;
    };
    const submitClinic = async () => {
      try {
        if (isEditMode.value && editingClinicId.value) {
          await updateClinic(editingClinicId.value, form.value);
          toast.success("Клиника успешно обновлена!");
        } else {
          const response = await createClinic(form.value);
          toast.success("Клиника создана! ID: " + response.data.ID);
        }
        resetForm();
        await fetchClinics();
      } catch (error) {
        toast.error("Ошибка при сохранении клиники");
        console.error(error);
      }
    };
    const removeClinic = async (id) => {
      const confirmed = window.confirm("Удалить эту клинику?");
      if (!confirmed) return;
      try {
        await deleteClinic(id);
        toast.success("Клиника удалена");
        await fetchClinics();
      } catch (err) {
        toast.error("Ошибка при удалении");
        console.error(err);
      }
    };
    const editClinic = (clinic) => {
      form.value = { ...clinic };
      editingClinicId.value = clinic.ID;
      isEditMode.value = true;
    };
    const cancelEdit = () => {
      resetForm();
    };
    const openDoctorForm = (clinicId) => {
      selectedClinicId.value = clinicId;
      showDoctorForm.value = true;
    };
    const onDoctorCreated = async () => {
      showDoctorForm.value = false;
      await fetchClinics();
    };
    const openClinicDetails = (clinic) => {
      selectedClinic.value = clinic;
    };
    onMounted(fetchClinics);

    return {
      clinics,
      form,
      isEditMode,
      submitClinic,
      removeClinic,
      editClinic,
      cancelEdit,
      showDoctorForm,
      selectedClinicId,
      openDoctorForm,
      onDoctorCreated,
      selectedClinic,
      openClinicDetails,
    };
  },
};
</script>

      <style scoped>
.input {
  @apply w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-green-300;
}
.btn {
  @apply px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition;
}
.btn-secondary {
  @apply px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 transition;
}
.btn-sm {
  @apply px-3 py-1 text-white rounded text-sm transition;
}
</style>
